/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { shallowEqual, useSelector } from "react-redux";
import { getUsedAssets, isCurrentUserOwner } from "utilities/ProjectUtilities";
import { shouldUseStreamingPlayer } from "./PlayerSelector";
import { AssetId, AssetSet } from "types/Asset";

export const useAssetsLoaded = (): boolean => {
  const isProjectOwner = useSelector(isCurrentUserOwner);
  const loaded = useSelector(
    (state) =>
      state.player.initialAssetsLoaded || shouldUseStreamingPlayer(state)
  );

  return !isProjectOwner || loaded;
};

export const useUsedAssets = (): AssetSet => {
  return useSelector(getUsedAssets, shallowEqual);
};

export const useAssetUploadProgress = (assetId: AssetId | null): number => {
  const uploadProgress = useSelector((state) => {
    if (!assetId) {
      return null;
    }

    const upload = state.assetUploads[assetId];
    return upload?.progress || 0;
  });

  return uploadProgress || 0;
};
